import React, { useState, useEffect } from 'react'
import { ArrowRightIcon, MenuIcon, XIcon, DocumentDownloadIcon } from "@heroicons/react/solid";

function Navbar() {
    const [isOpen, setIsOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);

    // Handle scroll effect
    useEffect(() => {
        const handleScroll = () => {
            const isScrolled = window.scrollY > 10;
            setScrolled(isScrolled);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // Close mobile menu when clicking on a link
    const closeMobileMenu = () => {
        setIsOpen(false);
    };

    return (
        <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
            scrolled 
                ? 'bg-gray-900 border-b border-gray-800/50 shadow-lg' 
                : 'bg-gray-900/90 backdrop-blur-sm'
        }`}>
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex items-center justify-between h-16 lg:h-20">
                    {/* Logo */}
                    <a href="#about" className="flex items-center space-x-3 hover:opacity-80 transition-opacity duration-200 group">
                        <div className="relative w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center overflow-hidden shadow-lg group-hover:shadow-blue-500/50 transition-all duration-300 group-hover:scale-105">
                            <img 
                                src="./favicon-32x32.png" 
                                alt="K" 
                                className="w-6 h-6 object-contain"
                            />
                        </div>
                        <span className="text-xl font-bold text-blue-500">
                            Kaleb Abiy
                        </span>
                    </a>

                    {/* Desktop Navigation */}
                    <nav className="hidden lg:flex items-center space-x-8">
                        <a 
                            href="#about" 
                            className="text-gray-300 hover:text-white transition-colors duration-200 relative group"
                        >
                            About
                            <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-blue-500 group-hover:w-full transition-all duration-300"></span>
                        </a>
                        <a 
                            href="#projects" 
                            className="text-gray-300 hover:text-white transition-colors duration-200 relative group"
                        >
                            Projects
                            <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-blue-500 group-hover:w-full transition-all duration-300"></span>
                        </a>
                        <a 
                            href="#skills" 
                            className="text-gray-300 hover:text-white transition-colors duration-200 relative group"
                        >
                            Skills
                            <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-blue-500 group-hover:w-full transition-all duration-300"></span>
                        </a>
                        <a
                            href="https://blog.kalebabiy.com"
                            className="text-gray-300 hover:text-white transition-colors duration-200 relative group"
                            rel="noopener noreferrer"
                            target="_blank"
                        >
                            Blogs
                            <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-blue-500 group-hover:w-full transition-all duration-300"></span>
                        </a>
                        <a 
                            href="https://docs.google.com/document/d/1Qosows2GRaqQxTGqMcIomZVVP-asURn2sUaWOYmw3yI/edit?usp=sharing" 
                            className="flex items-center space-x-2 text-gray-300 hover:text-white transition-colors duration-200 relative group"
                            rel="noopener noreferrer" 
                            target="_blank"
                        >
                            <DocumentDownloadIcon className="w-4 h-4" />
                            <span>CV</span>
                            <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-blue-500 group-hover:w-full transition-all duration-300"></span>
                        </a>
                    </nav>

                    {/* Desktop CTA Button */}
                    <a
                        href="#contact"
                        className="hidden lg:inline-flex items-center space-x-2 bg-blue-500 hover:bg-blue-600 text-white px-6 py-2.5 rounded-full font-medium transition-all duration-200 hover:scale-105 shadow-lg hover:shadow-xl"
                    >
                        <span>Get In Touch</span>
                        <ArrowRightIcon className="w-4 h-4" />
                    </a>

                    {/* Mobile menu button */}
                    <button
                        onClick={() => setIsOpen(!isOpen)}
                        className="lg:hidden p-2 rounded-md text-gray-300 hover:text-white hover:bg-gray-800 transition-colors duration-200"
                        aria-label="Toggle mobile menu"
                    >
                        {isOpen ? (
                            <XIcon className="w-6 h-6" />
                        ) : (
                            <MenuIcon className="w-6 h-6" />
                        )}
                    </button>
                </div>

                {/* Mobile Navigation Menu */}
                <div className={`lg:hidden transition-all duration-300 ease-in-out ${
                    isOpen 
                        ? 'max-h-96 opacity-100' 
                        : 'max-h-0 opacity-0 overflow-hidden'
                }`}>
                    <div className="mx-4 mt-4 mb-2 bg-white backdrop-blur-xl rounded-2xl shadow-2xl border border-gray-200 overflow-hidden">
                        <div className="p-6 space-y-2">
                            <a
                                href="#about"
                                onClick={closeMobileMenu}
                                className="block px-4 py-3 text-gray-700 hover:text-white hover:bg-blue-500 rounded-xl transition-all duration-200 font-medium"
                            >
                                About
                            </a>
                            <a
                                href="#projects"
                                onClick={closeMobileMenu}
                                className="block px-4 py-3 text-gray-700 hover:text-white hover:bg-blue-500 rounded-xl transition-all duration-200 font-medium"
                            >
                                Projects
                            </a>
                            <a
                                href="#skills"
                                onClick={closeMobileMenu}
                                className="block px-4 py-3 text-gray-700 hover:text-white hover:bg-blue-500 rounded-xl transition-all duration-200 font-medium"
                            >
                                Skills
                            </a>
                            <a
                                href="https://blog.kalebabiy.com"
                                onClick={closeMobileMenu}
                                className="block px-4 py-3 text-gray-700 hover:text-white hover:bg-blue-500 rounded-xl transition-all duration-200 font-medium"
                                rel="noopener noreferrer"
                                target="_blank"
                            >
                                Blogs
                            </a>
                            <a
                                href="https://docs.google.com/document/d/1Qosows2GRaqQxTGqMcIomZVVP-asURn2sUaWOYmw3yI/edit?usp=sharing"
                                onClick={closeMobileMenu}
                                className="flex items-center space-x-2 px-4 py-3 text-gray-700 hover:text-white hover:bg-blue-500 rounded-xl transition-all duration-200 font-medium"
                                rel="noopener noreferrer"
                                target="_blank"
                            >
                                <DocumentDownloadIcon className="w-4 h-4" />
                                <span>Download CV</span>
                            </a>
                            
                            {/* Separator */}
                            <div className="h-px bg-gray-300 my-4"></div>
                            
                            {/* CTA Button */}
                            <a
                                href="#contact"
                                onClick={closeMobileMenu}
                                className="flex items-center justify-center space-x-2 w-full bg-blue-500 hover:bg-blue-600 text-white px-6 py-4 rounded-xl font-semibold transition-all duration-200 shadow-lg hover:shadow-xl hover:scale-[1.02]"
                            >
                                <span>Get In Touch</span>
                                <ArrowRightIcon className="w-4 h-4" />
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        </header>
    )
}

export default Navbar



// WEBPACK FOOTER //
// ./src/components/Navbar.js